<template>
  <!-- Always rendered: the SCSS min-height reserves the strip's space from the
       first SSR paint, so nothing below jumps when brand data arrives.
       Hidden only once the API has confirmed there are no brands. -->
  <section
    class="vf-o-brands-slider-section no-col-padding"
    :class="{ hidden: brandsLoadedEmpty }"
  >
    <!-- Luxury brand marquee. One translateX offset (`pos`) is the single
         source of truth; the requestAnimationFrame loop writes the transform
         once per frame (smooth dragging on phones). A press does NOT capture the
         pointer, so a clean tap reaches the brand link and navigates; only once
         the pointer moves past a small threshold do we start dragging, capture,
         and suppress the click. When idle the velocity eases toward a slow
         drift so it resumes and gently matches speed the instant the user lets
         go. Sizing is driven by a ResizeObserver, so it measures correctly even
         on a hard refresh (server-render hydration), once images load, and on
         any resize. The offset wraps with a modulo on one set's width for an
         endless, seamless loop; the set is repeated to fill the viewport, and
         relayout never resets the position. -->
    <div
      ref="viewportEl"
      class="brands-marquee"
      @pointerdown="onPointerDown"
      @pointermove="onPointerMove"
      @pointerup="onPointerUp"
      @pointercancel="onPointerUp"
      @pointerleave="onPointerUp"
      @wheel="onWheel"
      @dragstart.prevent
    >
      <ul ref="trackEl" class="brands-track" aria-label="Brands we carry">
        <li
          v-for="(slide, index) in marqueeItems"
          :key="index"
          class="brand-item"
        >
          <router-link
            :to="slide.url"
            class="brand-link"
            draggable="false"
            :aria-hidden="index >= originalCount ? 'true' : null"
            :tabindex="index >= originalCount ? -1 : null"
            @click.capture="onClickCapture"
            @dragstart.prevent
          >
            <MarketingImage
              :media="brandMedia[String(slide.id)] || null"
              :fallback="slide.image"
              sizes="(max-width: 767px) 72px, 92px"
              :height="92"
              :width="92"
              loading="lazy"
              :alt="slide.name || 'brand'"
            />
          </router-link>
        </li>
      </ul>
    </div>
  </section>
</template>

<script setup lang="ts">
import {
  computed,
  nextTick,
  onBeforeUnmount,
  onMounted,
  onServerPrefetch,
  ref,
  watch,
} from "vue";
import { useStore } from "vuex";
import MarketingImage from "../../atoms/marketing-image/marketing-image.vue";
import type { MarketingMedia } from "../../../utils/marketingMedia";

const store = useStore();
const brandMedia = ref<Record<string, MarketingMedia>>({});
const REST_API_URL = (import.meta.env.VITE_REST_API_URL || "").replace(
  /\/+$/,
  ""
);

async function loadBrandMedia() {
  if (!REST_API_URL || Object.keys(brandMedia.value).length) return;
  try {
    const response = await fetch(
      `${REST_API_URL}?route=api%2Fhooligans_media%2Fbrands`
    );
    if (!response.ok) return;
    const payload = await response.json();
    if (payload?.media && typeof payload.media === "object")
      brandMedia.value = payload.media;
  } catch (_error) {
    // Existing category images remain the safe rollout fallback.
  }
}

const menuAPIkey = "categoriesList";
const categories = computed(
  () =>
    store.getters["apiHandler/getApiData"](menuAPIkey)?.categoriesList
      ?.content || []
);
const brandsCategoriesWithImagesOnly = computed(() => {
  const filteredImages = categories.value[2]?.categories.filter(
    (brand: any) => brand.image
  );
  return filteredImages;
});
const hasCategories = computed(
  () =>
    categories.value &&
    categories.value.length > 0 &&
    categories.value[2]?.categories?.length > 0
);
// True only after the categories API has responded AND there are no brands —
// the single case where collapsing the reserved strip is correct.
const brandsLoadedEmpty = computed(
  () =>
    !!store.getters["apiHandler/getApiData"](menuAPIkey) && !hasCategories.value
);
const sortByKey = (
  array: any[],
  key: string | number,
  order: "asc" | "desc" = "asc"
) =>
  [...(array || [])].sort((a, b) => {
    const valueA = a[key];
    const valueB = b[key];

    if (valueA < valueB) {
      return order === "asc" ? -1 : 1;
    }
    if (valueA > valueB) {
      return order === "asc" ? 1 : -1;
    }
    return 0;
  });

const sortedBrands = computed(() =>
  sortByKey(brandsCategoriesWithImagesOnly.value || [], "name")
);
const originalCount = computed(() => sortedBrands.value.length);

// Repeat the set enough times to always fill the viewport (+ one wrap span).
const repeatCount = ref(2);
const marqueeItems = computed(() => {
  const out: any[] = [];
  for (let i = 0; i < repeatCount.value; i++) {
    out.push(...sortedBrands.value);
  }
  return out;
});

const DRIFT_PX_PER_SEC = 55; // slow, deliberate auto-drift speed
const DRAG_THRESHOLD = 6; // px of movement before a press becomes a drag

// Pointer/trackpad: a longer, looser settle. Touch: snappier settle and a
// lower fling cap so a phone swipe does not coast too fast afterwards.
const MATCH_TAU = 0.8;
const MATCH_TAU_TOUCH = 0.3;
const MAX_FLING = 3000;
const MAX_FLING_TOUCH = 1200;
const TOUCH_FLING_SCALE = 0.65;

const viewportEl = ref<HTMLElement | null>(null);
const trackEl = ref<HTMLElement | null>(null);

let pos = 0; // translateX offset in px
let setWidth = 0; // width of one (non-repeated) set of logos
let pressing = false; // pointer is down (tap or drag)
let dragging = false; // moved past the threshold -> actively scrolling
let moved = false;
let activePointerId = -1;
let startX = 0;
let lastX = 0;
let lastMoveT = 0;
let velocity = 0; // px/s; eases toward the drift speed when released
let isTouch = false;
let reduceMotion = false;
let initialized = false;
let resizeObserver: ResizeObserver | null = null;
let rafId = 0;
let lastT = 0;

const now = () =>
  typeof performance !== "undefined" ? performance.now() : Date.now();

const wrap = () => {
  if (setWidth <= 0) return;
  pos %= setWidth;
  if (pos > 0) pos -= setWidth; // keep within (-setWidth, 0]
};

const apply = () => {
  if (trackEl.value) {
    trackEl.value.style.transform = `translate3d(${pos}px, 0, 0)`;
  }
};

// Measure one set's width and pick a repeat count that always overflows the
// viewport. Driven by a ResizeObserver, so it self-corrects after hydration,
// image loads, and resizes. Position is reset only on the first valid measure.
const recompute = () => {
  const vp = viewportEl.value;
  const tr = trackEl.value;
  if (!vp || !tr || originalCount.value === 0) return;

  const oneCopy = tr.scrollWidth / repeatCount.value;
  if (oneCopy <= 0) return; // not laid out yet; observer will fire again

  setWidth = oneCopy;
  const viewportWidth = vp.clientWidth || oneCopy;
  // One off-screen copy beyond the visible span is enough for a seamless wrap.
  const needed = Math.max(2, Math.ceil(viewportWidth / oneCopy) + 1);
  if (needed !== repeatCount.value) {
    // Re-render with more copies; the observer fires again and re-measures.
    repeatCount.value = needed;
  }

  if (!initialized) {
    pos = 0;
    initialized = true;
  }
  wrap();
  apply();
};

const observeEls = () => {
  if (typeof ResizeObserver === "undefined") return;
  if (!resizeObserver) resizeObserver = new ResizeObserver(() => recompute());
  resizeObserver.disconnect();
  if (viewportEl.value) resizeObserver.observe(viewportEl.value);
  if (trackEl.value) resizeObserver.observe(trackEl.value);
};

const onPointerDown = (e: PointerEvent) => {
  if (e.pointerType === "mouse" && e.button !== 0) return; // left button only
  // Do NOT capture here: a clean tap must still reach the brand link.
  pressing = true;
  dragging = false;
  moved = false;
  velocity = 0;
  isTouch = e.pointerType === "touch";
  activePointerId = e.pointerId;
  startX = e.clientX;
  lastX = e.clientX;
  lastMoveT = now();
};

// Record position/velocity only — the rAF loop writes the transform so the
// motion is aligned to the display refresh (no per-event paint jank).
const onPointerMove = (e: PointerEvent) => {
  if (!pressing || e.pointerId !== activePointerId) return;

  if (!dragging) {
    if (Math.abs(e.clientX - startX) <= DRAG_THRESHOLD) {
      lastX = e.clientX;
      lastMoveT = now();
      return; // still a potential tap; hold still
    }
    // Threshold crossed -> begin a real drag and capture the pointer so moves
    // continue if it leaves the strip.
    dragging = true;
    moved = true;
    try {
      viewportEl.value?.setPointerCapture(e.pointerId);
    } catch (err) {
      void err;
    }
  }

  const dx = e.clientX - lastX;
  pos += dx;
  const t = now();
  const dt = (t - lastMoveT) / 1000;
  if (dt > 0) velocity = 0.7 * velocity + 0.3 * (dx / dt);
  lastX = e.clientX;
  lastMoveT = t;
};

const onPointerUp = (e: PointerEvent) => {
  if (!pressing || e.pointerId !== activePointerId) return;
  pressing = false;
  activePointerId = -1;

  if (dragging) {
    dragging = false;
    // If the pointer paused before lifting, hand off at rest (no fling).
    if (now() - lastMoveT > 100) velocity = 0;
    if (isTouch) velocity *= TOUCH_FLING_SCALE;
    const cap = isTouch ? MAX_FLING_TOUCH : MAX_FLING;
    velocity = Math.max(-cap, Math.min(cap, velocity));
    try {
      viewportEl.value?.releasePointerCapture(e.pointerId);
    } catch (err) {
      void err;
    }
  }
};

const onWheel = (e: WheelEvent) => {
  // Only react to horizontal intent so vertical page scrolling passes through.
  if (Math.abs(e.deltaX) < Math.abs(e.deltaY)) return;
  e.preventDefault();
  isTouch = false;
  pos -= e.deltaX;
  velocity = 0;
};

// Suppress the link click only if the press turned into a drag.
const onClickCapture = (e: MouseEvent) => {
  if (moved) {
    e.preventDefault();
    e.stopPropagation();
    moved = false;
  }
};

const step = (t: number) => {
  if (!lastT) lastT = t;
  let dt = (t - lastT) / 1000;
  lastT = t;
  if (dt > 0.05) dt = 0.05; // clamp after the tab was backgrounded

  if (setWidth > 0) {
    if (!pressing) {
      // Ease the velocity toward the drift speed; the instant the user lets go
      // it starts matching the drift, smoothly (no delay, no snap). Touch uses
      // a shorter time constant so a swipe settles to the drift sooner.
      const target = reduceMotion ? 0 : -DRIFT_PX_PER_SEC;
      const tau = isTouch ? MATCH_TAU_TOUCH : MATCH_TAU;
      velocity += (target - velocity) * (1 - Math.exp(-dt / tau));
      pos += velocity * dt;
    }
    wrap();
    apply();
  }
  rafId = requestAnimationFrame(step);
};

onMounted(() => {
  if (typeof window === "undefined") return;
  reduceMotion = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
  observeEls();
  recompute();
  rafId = requestAnimationFrame(step);
  void loadBrandMedia();
});

onServerPrefetch(loadBrandMedia);

// Brand data loads asynchronously; once the section/refs exist, (re)observe
// and measure. Also covers a hard refresh where data is already present.
watch(originalCount, async (count) => {
  if (count > 0) {
    await nextTick();
    observeEls();
    recompute();
  }
});

onBeforeUnmount(() => {
  if (rafId) cancelAnimationFrame(rafId);
  if (resizeObserver) resizeObserver.disconnect();
});
</script>
